import { v4 as uuidv4 } from 'uuid';

import type { AgenticRoomScene, AgenticRoomScenes } from '../../store/modules/merchant/types';

/** Mirror the MMS limits, so a bad scene keeps Save disabled instead of failing on save. */
export const MAX_ROOM_SCENES = 5;
export const MAX_SCENE_NAME_LENGTH = 60;
export const MAX_SCENE_WHEN_TO_USE_LENGTH = 300;
export const ROOM_SCENE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const ROOM_SCENE_IMAGE_TYPES = ['image/jpeg', 'image/png'];

export const EMPTY_ROOM_SCENES: AgenticRoomScenes = { enabled: false, scenes: [] };

/** Set once and never changed: live sessions hold on to the id, so a rename must not strand them. */
const newSceneId = (): string => `scene-${uuidv4().slice(0, 8)}`;

/** The first scene becomes the default, so the list is never left without one. */
export const addRoomScene = (roomScenes: AgenticRoomScenes): AgenticRoomScenes => {
  const id = newSceneId();
  return {
    ...roomScenes,
    defaultSceneId: roomScenes.defaultSceneId ?? id,
    scenes: [...roomScenes.scenes, { id, name: '', whenToUse: '', image: { src: '' } }],
  };
};

export const removeRoomScene = (roomScenes: AgenticRoomScenes, id: string): AgenticRoomScenes => {
  const scenes = roomScenes.scenes.filter((scene) => scene.id !== id);
  if (scenes.length === 0) {
    return EMPTY_ROOM_SCENES;
  }
  return {
    ...roomScenes,
    scenes,
    defaultSceneId: roomScenes.defaultSceneId === id ? scenes[0].id : roomScenes.defaultSceneId,
  };
};

export const updateRoomScene = (
  roomScenes: AgenticRoomScenes,
  id: string,
  change: Partial<Omit<AgenticRoomScene, 'id'>>,
): AgenticRoomScenes => ({
  ...roomScenes,
  scenes: roomScenes.scenes.map((scene) => (scene.id === id ? { ...scene, ...change } : scene)),
});

const isHttpsUrl = (url: string | undefined): boolean => {
  try {
    return new URL(url ?? '').protocol === 'https:';
  } catch {
    return false;
  }
};

const isSceneValid = ({ name, whenToUse, image, imagePhone }: AgenticRoomScene): boolean => {
  const nameLength = name.trim().length;
  const whenLength = whenToUse.trim().length;
  return (
    nameLength > 0 &&
    nameLength <= MAX_SCENE_NAME_LENGTH &&
    whenLength > 0 &&
    whenLength <= MAX_SCENE_WHEN_TO_USE_LENGTH &&
    isHttpsUrl(image.src) &&
    (imagePhone === undefined || isHttpsUrl(imagePhone.src))
  );
};

export const isRoomScenesValid = ({
  enabled,
  defaultSceneId,
  scenes,
}: AgenticRoomScenes): boolean => {
  if (scenes.length === 0) {
    return !enabled;
  }
  return (
    scenes.length <= MAX_ROOM_SCENES &&
    scenes.some((scene) => scene.id === defaultSceneId) &&
    scenes.every(isSceneValid)
  );
};

/** Why a file can't be used, in the merchant's terms; undefined when it is fine. */
export const roomSceneImageError = (file: File): string | undefined => {
  if (!ROOM_SCENE_IMAGE_TYPES.includes(file.type)) {
    return 'Use a JPEG or PNG image.';
  }
  if (file.size > ROOM_SCENE_IMAGE_MAX_BYTES) {
    return 'Use an image of 10 MB or less.';
  }
  return undefined;
};
